import React, { FC, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Flex, Text } from "@radix-ui/themes";
import { DateTime } from "luxon";
import { UuidFieldRow } from "@/components/table/uuid-field-row";

import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";

type CapturedEmailSummarySchema = {
  id: string;
  message_id: string;
  thread_id: string;
  sender: string;
  subject: string;
  internal_date: string;
};

export const CapturedEmailsRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = 50;
  const initialQuery = searchParams.get("query") ?? "";
  const [searchTerm, setSearchTerm] = useState<string>(initialQuery);
  const [inputValue, setInputValue] = useState<string>(initialQuery);
  const isUuid = (val: string) =>
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(
      val
    );

  useEffect(() => {
    const query = searchParams.get("query") ?? "";
    setSearchTerm(query);
    setInputValue(query);
  }, [searchParams]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["captured-emails-search", searchTerm],
    queryFn: async () => {
      const term = searchTerm.trim();
      if (!term) return { data: { results: [], total: 0 } };

      type SearchCapturedEmailsResponse = { data?: { results: CapturedEmailSummarySchema[] } };
      const requests: Promise<SearchCapturedEmailsResponse>[] = [];
      if (isUuid(term)) {
        requests.push(
          api.post("/internal/v1/captured-emails/search", {
            body: { limit: pageSize, filter_by_id: term },
          })
        );
      }
      requests.push(
        api.post("/internal/v1/captured-emails/search", {
          body: { limit: pageSize, filter_by_message_id: term },
        })
      );
      requests.push(
        api.post("/internal/v1/captured-emails/search", {
          body: { limit: pageSize, filter_by_thread_id: term },
        })
      );
      requests.push(
        api.post("/internal/v1/captured-emails/search", {
          body: { limit: pageSize, filter_by_sender_contains: term },
        })
      );
      requests.push(
        api.post("/internal/v1/captured-emails/search", {
          body: { limit: pageSize, filter_by_subject_contains: term },
        })
      );

      const responses = await Promise.all(requests);
      const all: CapturedEmailSummarySchema[] = responses.flatMap(r => r.data?.results ?? []);
      const uniqById: CapturedEmailSummarySchema[] = Array.from(
        new Map(all.map((row: CapturedEmailSummarySchema) => [row.id, row])).values()
      );
      // Order by internal_date desc (most recent first)
      uniqById.sort(
        (a: CapturedEmailSummarySchema, b: CapturedEmailSummarySchema) =>
          new Date(b.internal_date).valueOf() - new Date(a.internal_date).valueOf()
      );
      return { data: { results: uniqById, total: uniqById.length } };
    },
    enabled: searchTerm.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const handleSearch = (query: string) => {
    if (query) {
      searchParams.set("query", query);
    } else {
      searchParams.delete("query");
    }
    setSearchParams(searchParams);
  };

  const handleView = (id: string) => {
    navigate(atlasRoutes.capturedEmailsView({ params: { capturedEmailId: id } }).path);
  };

  const tableData = useMemo<CapturedEmailSummarySchema[]>(() => data?.data?.results ?? [], [data]);

  return (
    <Container>
      <Flex justify="between" align="center" mb="4">
        <div>
          <h1>Captured Emails</h1>
          <DescriptionText>
            Search by Record ID, Message ID, Thread ID, Sender, or Subject.
          </DescriptionText>
        </div>
      </Flex>

      <SearchRow>
        <input
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") handleSearch(inputValue);
          }}
          placeholder="Search by ID, Message ID, Thread ID, Sender, or Subject"
        />
        <Button onClick={() => handleSearch(inputValue)}>Search</Button>
      </SearchRow>

      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching: {error.message}</div>}

      {tableData.length > 0 ? (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Created At</th>
                <th>ID</th>
                <th>Message</th>
                <th>Thread</th>
                <th>Sender</th>
                <th>Subject</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map(row => (
                <tr key={row.id}>
                  <td>
                    {row.internal_date
                      ? DateTime.fromISO(row.internal_date).toFormat("MMM d, yyyy h:mm a")
                      : "-"}
                  </td>
                  <td>
                    <UuidFieldRow id={row.id as unknown as string} />
                  </td>
                  <td>{row.message_id}</td>
                  <td>{row.thread_id}</td>
                  <td>{row.sender}</td>
                  <td>{row.subject}</td>
                  <td>
                    <Button variant="soft" onClick={() => handleView(row.id)}>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      ) : (
        !isLoading && searchTerm && <div>No records found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;

const SearchRow = styled(Flex)`
  gap: 8px;
  margin: 12px 0 16px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th,
  td {
    text-align: left;
    padding: 8px 6px;
    border-bottom: 1px solid var(--gray-5);
    white-space: nowrap;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;
